import Cookies from 'js-cookie';
import {getEnvConfig} from '@/utils/getEnvConfig';
import getGameId from '@/utils/getGameId';
import {SKILLZ_APP_GAME_ID} from '@/app/(app)/partner/[slug]/_src/shellGame';
import {PARTNER_SESSION_MINT_FLAG} from '@/utils/constant';
import {remapShellPath} from '@/utils/shellRoute';
import {signupUrl, liveEventSignupUrl} from '@/utils/signup';
import auth from '../auth/AuthQueue';

/** The account-creation / auth screens where "no user id yet" is EXPECTED (a
 *  deferred-auth guest lands here WITHOUT a session on purpose). Mirrors
 *  AuthHandler's `isRegistering` guard. */
const AUTH_CREATION_SEGMENTS = new Set([
  signupUrl, // 'register' (or 'sign-in' when phone-first)
  liveEventSignupUrl, // 'play-now'
  'sign-in',
  'signup',
  'tutorial',
]);

export function getUserId(shouldLogOut = true) {
  const COOKIE_NAME = getEnvConfig().CURRENT_USER_COOKIE_NAME;
  const id = Cookies.get(COOKIE_NAME) as string;
  console.log('getUserId: Cookie name:', COOKIE_NAME);
  console.log('getUserId: User ID from cookie:', id);
  if (!id && shouldLogOut) {
    // Don't log out + hard-redirect to /register when we're ALREADY on an
    // account-creation / auth screen: the redirect target IS this page, so
    // window.location.replace reloads it, getUserId runs again with no id, and it
    // loops forever (the reported infinite /register reload). On those routes a
    // missing id is normal, not a lost session — return '' quietly. Everywhere else
    // (game home/store/account/deposit) the lost-session logout still fires.
    const seg =
      typeof window !== 'undefined'
        ? window.location.pathname.split('/').filter(Boolean).pop()
        : undefined;
    if (seg && AUTH_CREATION_SEGMENTS.has(seg)) {
      return '';
    }

    // The multicade SHELL surface (root "/" + partner arcade: catalogue, store,
    // account, menu — all backed by game 19369) is a DEFERRED-AUTH surface that mints
    // and revives its OWN anon session (PartnerSdkRuntime). On a fresh visit the anon
    // mint is still in flight, so "no user id yet" is EXPECTED here exactly like the
    // auth screens — NOT a lost session to log out of. Firing the hard logout+redirect
    // to /register in that window is what yanked a fresh partner guest OFF the catalogue
    // (killing the FTUE overlay before it could show) into a 401-ing register page. Return
    // '' quietly and let the runtime's mint finish; a genuinely lapsed shell token is
    // handled by the runtime's own refresh, never by a logout-and-register bounce.
    if (getGameId() === String(SKILLZ_APP_GAME_ID)) {
      return '';
    }

    auth.logout().then(() => {
      console.warn('getUserId: User ID not found, logging out and redirecting to register page');
      // This is a HARD navigation, so it never passes through RouteHandler's remapUrl —
      // remap here or the multicade shell (getGameId() === 19369) lands the browser on
      // `/games/19369/register`, which is exactly the URL the clean-route migration
      // exists to keep out of the address bar. Real games are returned unchanged.
      // Clear the per-tab mint flag for the same reason NativeNavigation.exitSkillz does:
      // auth.logout() above just destroyed the session, and sessionStorage survives this
      // navigation, so without clearing it the partner runtime never re-mints and every
      // later visit to `/partner/{slug}` in THIS tab dead-ends here.
      try {
        sessionStorage.removeItem(PARTNER_SESSION_MINT_FLAG);
      } catch {
        /* sessionStorage unavailable — nothing to clear */
      }
      window.location.replace(remapShellPath(`/games/${getGameId()}/register`));
    });
    return '';
  }

  return id;
}
